// lib/leasing/cnccReferential.ts
//
// The CNCC 3-level activity referential (famille > sous-famille > secteur) used by
// French landlords' fiches navettes. The 7 familles (A-G) are the official CNCC
// groups (see CNCC_SECTORS in types); the sous-familles/secteurs below are the
// standard industry funnel, curated for sourcing (2026-07-16 leasing call: "chaussures
// mixtes" tells a leasing manager instantly what a brand is; "fashion and apparel"
// does not). Editable: extending a list is safe, classification validates against it.
import { CNCC_SECTORS, type CNCCSector } from '#types/types';

export interface CnccSubFamily {
  name: string;
  secteurs: string[];
}

const CNCC_REFERENTIAL: Record<CNCCSector, CnccSubFamily[]> = {
  A: [
    { name: 'Alimentation spécialisée', secteurs: ['Boulangerie-Pâtisserie', 'Boucherie-Charcuterie-Traiteur', 'Chocolaterie-Confiserie', 'Caviste', 'Produits bio et diététiques', 'Épicerie fine', 'Primeur', 'Fromagerie', 'Poissonnerie', 'Surgelés', 'Torréfacteur-Thés et cafés'] },
    { name: 'Grandes surfaces alimentaires', secteurs: ['Hypermarché', 'Supermarché', 'Supérette', 'Marché frais'] },
  ],
  B: [
    { name: 'Prêt-à-porter', secteurs: ['Prêt-à-porter féminin', 'Prêt-à-porter masculin', 'Prêt-à-porter enfant', 'Prêt-à-porter mixte', 'Grandes tailles', 'Mode sportswear', 'Jeanning', 'Mode grande distribution'] },
    { name: 'Chaussures', secteurs: ['Chaussures femme', 'Chaussures homme', 'Chaussures enfant', 'Chaussures mixtes', 'Chaussures de sport (sneakers)'] },
    { name: 'Lingerie', secteurs: ['Lingerie féminine', 'Lingerie masculine', 'Lingerie mixte', 'Chaussant-Collants'] },
    { name: 'Accessoires et maroquinerie', secteurs: ['Maroquinerie-Bagagerie', 'Accessoires de mode', 'Bijouterie fantaisie', 'Chapellerie'] },
    { name: 'Bijouterie-Horlogerie', secteurs: ['Bijouterie-Joaillerie', 'Horlogerie'] },
    { name: 'Optique et audition', secteurs: ['Optique-Lunetterie', 'Audioprothèse'] },
  ],
  C: [
    { name: 'Meubles et décoration', secteurs: ['Ameublement', 'Décoration', 'Arts de la table', 'Linge de maison', 'Luminaires', 'Literie'] },
    { name: 'Électroménager et multimédia', secteurs: ['Électroménager', 'Image-Son-Multimédia', 'Téléphonie', 'Informatique'] },
    { name: 'Bricolage et jardinage', secteurs: ['Bricolage', 'Jardinerie', 'Quincaillerie'] },
    { name: 'Cuisines et bains', secteurs: ['Cuisines équipées', 'Salles de bains'] },
  ],
  D: [
    { name: 'Culture', secteurs: ['Librairie-Papeterie-Presse', 'Produits culturels et multimédia', 'Musique et instruments'] },
    { name: 'Jeux et jouets', secteurs: ['Jouets', 'Jeux vidéo', 'Jeux de société-Figurines'] },
    { name: 'Sport', secteurs: ['Articles de sport généralistes', 'Sport spécialisé (glisse, running, outdoor)', 'Cycles'] },
    { name: 'Cadeaux et divers', secteurs: ['Cadeaux-Gadgets', 'Fleuriste', 'Animalerie', 'Tabac-Cigarette électronique', 'Loisirs créatifs'] },
    { name: 'Voyages et loisirs', secteurs: ['Agence de voyages', 'Billetterie-Loisirs', 'Loisirs indoor (escape game, bowling, trampoline)'] },
  ],
  E: [
    { name: 'Beauté', secteurs: ['Parfumerie', 'Cosmétiques', 'Institut de beauté', 'Onglerie'] },
    { name: 'Coiffure', secteurs: ['Salon de coiffure', 'Barbier'] },
    { name: 'Santé', secteurs: ['Pharmacie', 'Parapharmacie', 'Matériel médical', 'Centre médical ou dentaire', 'Diététique-Nutrition'] },
    { name: 'Forme et bien-être', secteurs: ['Salle de sport-Fitness', 'Bien-être (spa, massage)', 'Hygiène et soins du corps'] },
  ],
  F: [
    { name: 'Services financiers', secteurs: ['Banque', 'Assurance', 'Courtage-Change'] },
    { name: 'Services à la personne', secteurs: ['Pressing-Retoucherie', 'Cordonnerie-Clés-Multiservices', 'Photo-Reprographie', 'Relais colis-Poste', 'Réparation mobile et informatique'] },
    { name: 'Services commerciaux', secteurs: ['Agence immobilière', 'Agence intérim-Emploi', 'Opérateur téléphonique', 'Location de véhicules', 'Auto-école'] },
  ],
  G: [
    { name: 'Restauration rapide', secteurs: ['Fast-food', 'Sandwicherie-Snacking', 'Vente à emporter', 'Coffee shop', 'Pizzeria à emporter', 'Bagels-Burgers', 'Poké-Sushis à emporter'] },
    { name: 'Restauration à table', secteurs: ['Restaurant traditionnel', 'Restaurant à thème', 'Brasserie', 'Pizzeria-Italien', 'Cuisine du monde', 'Grill-Viandes'] },
    { name: 'Cafés, bars et douceurs', secteurs: ['Bar-Café', 'Salon de thé', 'Glacier', 'Jus et smoothies', 'Pâtisserie-Douceurs sur place'] },
  ],
};

/** Validate an LLM-proposed triple against the referential (exact labels only). */
export const isValidCnccTriple = (sector: string, subFamily: string, secteur: string): boolean => {
  const families = CNCC_REFERENTIAL[sector as CNCCSector];
  if (!families) return false;
  const sf = families.find((f) => f.name === subFamily);
  return !!sf && sf.secteurs.includes(secteur);
};

/** Render the referential as compact prompt text the classifier chooses from. */
export const formatCnccReferentialForPrompt = (): string =>
  (Object.keys(CNCC_REFERENTIAL) as CNCCSector[])
    .map((sector) => {
      const famille = CNCC_SECTORS[sector];
      const subs = CNCC_REFERENTIAL[sector]
        .map((sf) => `  - ${sf.name}: ${sf.secteurs.join(' | ')}`)
        .join('\n');
      return `${sector}: ${famille}\n${subs}`;
    })
    .join('\n');

// --- Secteur-level filtering support (2026-07-16 call: filter "marques de basket",
// not just the 7 broad familles). The campaign CNCC filter picks exact secteurs. ---

export interface CnccFamilleGroup {
  sector: CNCCSector; // A-G
  famille: string; // e.g. "Équipement de la personne"
  secteurs: string[]; // flattened across sous-familles, e.g. "Chaussures mixtes"
}

/** The referential flattened to famille → its secteurs, for a grouped picker. */
export const cnccFamilleGroups = (): CnccFamilleGroup[] =>
  (Object.keys(CNCC_REFERENTIAL) as CNCCSector[]).map((sector) => ({
    sector,
    famille: CNCC_SECTORS[sector],
    secteurs: CNCC_REFERENTIAL[sector].flatMap((sf) => sf.secteurs),
  }));

// secteur label -> its famille letter (first match wins; secteur labels are unique
// across familles in the referential).
const SECTEUR_TO_SECTOR: Record<string, CNCCSector> = (() => {
  const map: Record<string, CNCCSector> = {};
  for (const sector of Object.keys(CNCC_REFERENTIAL) as CNCCSector[]) {
    for (const sf of CNCC_REFERENTIAL[sector]) {
      for (const secteur of sf.secteurs) if (!(secteur in map)) map[secteur] = sector;
    }
  }
  return map;
})();

export const familleOfSecteur = (secteur: string): CNCCSector | null =>
  SECTEUR_TO_SECTOR[secteur] ?? null;

export const isValidCnccSecteur = (secteur: string): boolean => secteur in SECTEUR_TO_SECTOR;

/** "Chaussures mixtes · Équipement de la personne (B)" or the famille alone. */
export const formatCnccPath = (
  b: { cnccSector?: string | null; cnccSubFamily?: string | null; cnccActivity?: string | null },
): string | null => {
  const sector = b.cnccSector?.trim().toUpperCase();
  const famille = sector ? CNCC_SECTORS[sector as CNCCSector] : undefined;
  if (!famille || !sector) return null;
  if (b.cnccActivity) return `${b.cnccActivity} · ${famille} (${sector})`;
  if (b.cnccSubFamily) return `${b.cnccSubFamily} · ${famille} (${sector})`;
  return `${famille} (${sector})`;
};
